import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Typography,
  Image,
  Input,
  Divider,
  Checkbox,
  theme,
  Form,
  Button,
  notification,
  Tooltip,
  Select,
  InputNumber
} from "antd";
import styles from "../styles/InvoicePayment.module.css";
import { useBrandContext } from "../contexts/Brand";
import { useServer } from "../contexts/Server";
import {
  screenWidths,
  validateEmail,
  rfx_v2_xnew_transformOrderObject,
  containsLetters,
  decodeParamString,
  encodeParamString,
  formatNumberWithCommas,
} from "../const/const_functions";
import { AiFillStar } from "react-icons/ai";
import useWindowDimensions from "../hooks/useWindowDimensions";
import { useNavigate, useParams } from "react-router-dom";
import { FaInfoCircle } from "react-icons/fa";

const allRules = {
  amountToPay: [
    {
      required: true,
      message: "Please input Payment Amount!",
    },
    {
      validator: (_, value) => {
        if (value && value <= 0) {
          return Promise.reject(new Error("Payment amount is invalid"));
        }
        return Promise.resolve();
      },
    },
  ],
  customerEmail: [
    {
      required: true,
      message: "Please input your Email!",
    },
    { whitespace: true },
    {
      validator: (_, value) => {
        const trimmedValue = value.trim();
        if (trimmedValue && !validateEmail(trimmedValue)) {
          return Promise.reject(new Error("Please input a valid email"));
        }

        return Promise.resolve();
      },
    },
  ],
  customerPhone: [
    { whitespace: false },
    {
      validator: (_, value) => {
        if (Number(value) < 0) {
          return Promise.reject(
            new Error("Please enter a valid 10 digit phone number.")
          );
        }

        if (isNaN(Number(value))) {
          return Promise.reject(
            new Error("Phone Numbers should only consist of digits.")
          );
        }

        if (value && value.length !== 10) {
          return Promise.reject(
            new Error("Phone Numbers should be 10 digits long.")
          );
        }

        return Promise.resolve();
      },
    },
  ],
};

const Bird = {
  accountId: {
    label: "Account Number",
    type: "or",
    sibling: "invoiceId",
    display: true,
    allowSave: true,
  },
  invoiceId: {
    label: "Invoice Number",
    sibling: "accountId",
    type: "or",
    display: false,
    allowSave: false,
  },
  amountToPay: {
    label: "Payment Amount",
    type: "required",
    display: true,
    allowSave: true,
  },
  serviceDescription: {
    label: "Service Description",
    type: "required",
    display: true,
    allowSave: true,
  },
  customerName: {
    label: "Personal/Business Name",
    type: "required",
    display: true,
    allowSave: true,
  },
  customerEmail: {
    label: "Email",
    type: "required",
    display: true,
    allowSave: true,
  },
  customerPhone: {
    label: "Phone",
    type: "optional",
    display: true,
    allowSave: true,
  },
};

const { Text } = Typography;

const InvoicePaymentForm = () => {
  const { brandContextData, loadingBrandContextData } = useBrandContext();
  const { serverData } = useServer();
  const { useToken } = theme;
  const navigate = useNavigate();
  const { token } = useToken();
  const [form] = Form.useForm();

  const { encodedStadiumId } = useParams();
  const stadiumId = decodeParamString(encodedStadiumId);

  const [stadiumData, setStadiumData] = useState(null);

  const [saveInfoForNextTime, setSaveInfoForNextTime] = useState(true);
  const [showSaveCheckbox, setShowSaveCheckbox] = useState(true);
  // No longer used
  // const [accountNumber, setAccountNumber] = useState("");
  // const [email, setEmail] = useState("");
  // const [name, setName] = useState("");
  // const [serviceDetails, setServiceDetails] = useState("");
  // const [phone, setPhone] = useState("");
  // const [invoiceNumber, setInvoiceNumber] = useState("");

  const [orderTotalWithEverything, setOrderTotalWithEverything] = useState(0);
  const [orderSubtotal, setOrderSubtotal] = useState(0);
  const [digitalFeeObj, setDigitalFeeObj] = useState(null);
  const [serviceFeeToShow, setServiceFeeToShow] = useState(0);
  const [serviceFeeAgreement, setServiceFeeAgreement] = useState(false);

  const [submittable, setSubmittable] = useState(false);
  const [creatingOrder, setCreatingOrder] = useState(false);

  const [invoiceDetails, setInvoiceDetails] = useState({});

  let mobile = false;
  const { width } = useWindowDimensions();
  if (screenWidths.md > width) {
    mobile = true;
  }

  useEffect(() => {
    handleFormChange();
  }, [serviceFeeAgreement]);

  useEffect(() => {
    // Sync form values with state
    form.setFieldsValue(invoiceDetails);
  }, [invoiceDetails, form]);

  const checkForSavedInfo = () => {
    const userInfo = JSON.parse(
      localStorage.getItem("userInfoForInvoiceCheckout")
    );
    if (userInfo) {
      setInvoiceDetails(userInfo);
    }
  };

  useEffect(() => {
    if (serverData) {
      getStadiumData();
      getServiceFee();
      checkForSavedInfo();
    }
  }, [serverData]);

  useEffect(() => {
    if (digitalFeeObj) {
      computeOrderTotal();
    }
  }, [orderSubtotal]);

  const validateInvoiceDetails = (invoiceDetails) => {
    const errors = {};

    const requiredFields = new Set();

    if (Object.keys(invoiceDetails).length === 0) {
      return { noInvoiceDetails: "There are no invoice details" };
    }

    if (!stadiumData?.invoiceTemplate) {
      return errors;
    }

    Object.keys(stadiumData?.invoiceTemplate).forEach((field) => {
      const { type, sibling, display, label } = stadiumData.invoiceTemplate[field];

      if (display) {
        const value = invoiceDetails[field];

        if (type === "required" && (!value || String(value).trim() === "")) {
          errors[field] = `${label} is required.`;
        }

        if (sibling) {
          requiredFields.add(field);

          if (invoiceDetails[field] && invoiceDetails[sibling]) {
            // If both siblings are present, clear the errors for both
            delete errors[field];
            delete errors[sibling];
          }
        }
      }
    });

    // Check if at least one field from each sibling pair is present
    requiredFields.forEach((field) => {
      const sibling = stadiumData.invoiceTemplate[field].sibling;
      if (!invoiceDetails[field] && !invoiceDetails[sibling]) {
        errors[
          field
        ] = `${stadiumData.invoiceTemplate[field].label} or ${stadiumData.invoiceTemplate[sibling].label} is required.`;
      }
    });

    return errors;
  };

  const getStadiumData = async () => {
    setCreatingOrder(true);
    try {
      const stadiumData = await serverData.call(
        "USSR_getOneStadium",
        stadiumId
      );
      if (stadiumData) {
        const _ = require("lodash");
        const newStadiumData = _.cloneDeep(stadiumData);
        newStadiumData.stadTypes = ["STT_INVOICE"];

        if (!newStadiumData.invoiceTemplate) {
          // Mocked templating
          newStadiumData.invoiceTemplate = Bird;
        }

        let isOneAllowSaveTrue = false;
        for (const key in newStadiumData.invoiceTemplate) {
          if (newStadiumData.invoiceTemplate.hasOwnProperty(key)) {
            if (newStadiumData.invoiceTemplate[key].allowSave === true) {
              isOneAllowSaveTrue = true;
              break;
            }
          }
        }

        if (!isOneAllowSaveTrue) {
          setSaveInfoForNextTime(false);
          setShowSaveCheckbox(false);
        }

        setStadiumData(newStadiumData);
      } else {
        navigate("/error");
      }
    } catch (e) {
      console.log(e);
      navigate("/error");
    }
    setCreatingOrder(false);
  };

  const getServiceFee = async () => {
    // Compute order digital fee
    let digitalFeeObj = {};
    try {
      digitalFeeObj = await serverData.call("MIKO_getContextServiceFee", {
        contextId: brandContextData._id,
        exemptFee: stadiumData && stadiumData.catalogueMode ? true : false,
      });
      setDigitalFeeObj(digitalFeeObj);
    } catch (e) {
      console.log(e);
      return;
    }
  };

  const computeOrderTotal = () => {
    let serviceFee = 0;
    if (digitalFeeObj?.applyServiceFee) {
      const feePercentage = digitalFeeObj.serviceFeeInfo.feePercentage;
      const feeCents = digitalFeeObj.serviceFeeInfo.feeCents;

      serviceFee = orderSubtotal * (feePercentage / 100) + feeCents / 100;
    }
    setServiceFeeToShow(serviceFee);

    setOrderTotalWithEverything(orderSubtotal + serviceFee);
  };

  const saveThisInfoForNextSession = () => {
    let userInfoForInvoiceCheckout = {};
    for (const [key, value] of Object.entries(stadiumData.invoiceTemplate)) {
      if (value.allowSave && invoiceDetails[key]) {
        userInfoForInvoiceCheckout[key] = invoiceDetails[key];
      }
    }

    localStorage.removeItem("userInfoForInvoiceCheckout");

    if (saveInfoForNextTime) {
      localStorage.setItem(
        "userInfoForInvoiceCheckout",
        JSON.stringify(userInfoForInvoiceCheckout)
      );
    }
  };

  const createTheOrder = async () => {
    saveThisInfoForNextSession();

    let paymentInfo = {
      totalAmount: orderTotalWithEverything,
      hst: 0,
      deliveryFee: null,
      orderAmount: orderSubtotal,
      appVersion: null,
      platform: "web",
    };

    let region = null;
    let storeInstructions = "N/A";

    const dataObjToBeTransformed = {
      foodItems: [],
      amount: orderTotalWithEverything,
      address: null,
      deliveryComment: null,
      note: storeInstructions,
      deliveryOption: "invoice",
      stadium: stadiumData,
      paymentInfo,
      zone: region,
      invoicesToPay: [
        {
          accountId: invoiceDetails["accountId"],
          invoiceId: invoiceDetails["invoiceId"],
          serviceDetails: invoiceDetails["serviceDescription"]
            ? invoiceDetails["serviceDescription"]
            : null,
          amountToPay: orderSubtotal,
        },
      ],
    };

    let transformedDataObj = null;
    transformedDataObj = rfx_v2_xnew_transformOrderObject(
      dataObjToBeTransformed
    );

    transformedDataObj.guestInfo = {
      name: invoiceDetails["customerName"],
      phoneNumber: invoiceDetails["customerPhone"]
        ? invoiceDetails["customerPhone"]
        : null,
      email: invoiceDetails["customerEmail"].trim(),
    };

    transformedDataObj.guestCheckout = true;

    transformedDataObj.contextId = brandContextData._id;
    let orderID = null;
    try {
      const res = await serverData.call(
        "rfx_v2_xmod_addOrder",
        transformedDataObj
      );
      orderID = res;
      const encodedOrderId = encodeParamString(orderID);
      const feeLabel = digitalFeeObj?.serviceFeeInfo?.feeLabel
        ? digitalFeeObj.serviceFeeInfo.feeLabel
        : null;
      const newStoreUrl = `/${encodedOrderId}/payinvoice`;
      navigate(newStoreUrl, {
        state: {
          orderID,
          encodedStadiumId,
          feeLabel,
        },
      });
    } catch (e) {
      notification.error({
        message: "Failed to Create Payable Invoice",
        description: e.reason,
      });
      return;
    }
  };

  const handleFormChange = () => {
    let isFormValid = false;
    const hasErrors = form.getFieldsError().some(({ errors }) => errors.length);

    const validationErrors = validateInvoiceDetails(invoiceDetails);

    if (Object.keys(validationErrors).length === 0 && !hasErrors) {
      if (!digitalFeeObj?.applyServiceFee) {
        isFormValid = true;
      }

      if (digitalFeeObj?.applyServiceFee && serviceFeeAgreement) {
        isFormValid = true;
      }
    } else {
      // console.log("Validation errors:", validationErrors);
    }

    setSubmittable(isFormValid);
  };

  let submitButtonTitle = "Input Payment Amount";
  if (orderSubtotal && !creatingOrder) {
    submitButtonTitle = `Continue to Payment - $${formatNumberWithCommas(
      orderTotalWithEverything
    )}`;
  } else if (orderSubtotal && creatingOrder) {
    submitButtonTitle = "Creating order...";
  }

  const _renderFormField = (formObj, fieldName, formFields) => {
    const { display, label, type, sibling = false, selectorOptions = [], linkedSelector = null } = formObj;

    const handleLinkedChange = ({ inputVal, fieldName }) => {
      takeInput({ inputVal, fieldName });

      if (linkedSelector) {
        const linkObj = formFields.find(frm => frm.key === linkedSelector);
        const linkOptions = linkObj && linkObj.selectorOptions && Array.isArray(linkObj.selectorOptions) ? linkObj.selectorOptions : [];
        const linkedIndex = selectorOptions.findIndex(elem => elem === inputVal);

        if (linkOptions && linkOptions[linkedIndex]) {
          takeInput({ inputVal: linkOptions[linkedIndex], fieldName: linkedSelector });
        }
      }
    }

    const showFormField = display;
    const formLabel = label;
    const isRequired = type === "required";

    const txtLabel = <Text className={styles.grayText}>{formLabel}</Text>;
    const requiredStar = isRequired ? (
      <AiFillStar
        fontSize={5}
        color="red"
        style={{ marginTop: 2, marginRight: 3 }}
      />
    ) : null;

    if (!showFormField) {
      return null;
    }

    if (sibling) {
      const siblingObj = stadiumData.invoiceTemplate[sibling];
      return (
        <Form.Item
          initialValue={invoiceDetails[fieldName]}
          className={styles.formItems}
          name={fieldName}
          rules={[
            {
              validator: (_, value) => {
                if (!value && !invoiceDetails[fieldName]) {
                  return Promise.reject(
                    new Error(
                      `Please input ${formLabel} and/or ${siblingObj.label}!`
                    )
                  );
                }

                return Promise.resolve();
              },
            },
          ]}
        >
          <Row style={{ alignItems: "center" }}>
            <Col
              xs={{ span: 9 }}
              sm={{ span: 9 }}
              md={{ span: 10 }}
              lg={{ span: 10 }}
              xl={{ span: 11 }}
            >
              <Row>
                <AiFillStar
                  fontSize={5}
                  color="red"
                  style={{ marginTop: 2, marginRight: 3 }}
                />
                <Text className={styles.grayText}>{formLabel}</Text>
              </Row>
              {
                Array.isArray(selectorOptions) && selectorOptions.length > 0 ?
                  <Select
                    style={{
                      width: "100%",
                    }}
                    value={invoiceDetails[fieldName]}
                    onChange={(e) => handleLinkedChange({ inputVal: e, fieldName })}
                    options={selectorOptions.map(
                      (option) => ({
                        value: option,
                        label: fieldName === "amountToPay" ? `$${option}` : option,
                      })
                    )}
                  />
                  :
                  <Input
                    value={invoiceDetails[fieldName]}
                    onChange={(e) =>
                      takeInput({ inputVal: e.target.value, fieldName })
                    }
                  />
              }
            </Col>
            <Col
              xs={{ span: 6 }}
              sm={{ span: 6 }}
              md={{ span: 4 }}
              lg={{ span: 4 }}
              xl={{ span: 2 }}
              className={styles.spaceCol}
            >
              <Text className={styles.text}>and/or</Text>
            </Col>
            <Col
              xs={{ span: 9 }}
              sm={{ span: 9 }}
              md={{ span: 10 }}
              lg={{ span: 10 }}
              xl={{ span: 11 }}
            >
              <Row>
                <AiFillStar
                  fontSize={5}
                  color="red"
                  style={{ marginTop: 2, marginRight: 3 }}
                />
                <Text className={styles.grayText}>{siblingObj.label}</Text>
              </Row>
              {
                Array.isArray(selectorOptions) && selectorOptions.length > 0 ?
                  <Select
                    style={{
                      width: "100%",
                    }}
                    value={invoiceDetails[sibling]}
                    onChange={(e) => handleLinkedChange({ inputVal: e, fieldName: sibling })}
                    options={selectorOptions.map(
                      (option) => ({
                        value: option,
                        label: fieldName === "amountToPay" ? `$${option}` : option,
                      })
                    )}
                  />
                  :
                  <Input
                    value={invoiceDetails[sibling]}
                    onChange={(e) =>
                      takeInput({ inputVal: e.target.value, fieldName: sibling })
                    }
                  />
              }
            </Col>
          </Row>
        </Form.Item>
      );
    }

    let rules = [];
    if (allRules[fieldName]) {
      const { _id: contextId } = brandContextData;

      // Custom rule for jepson petroleum (main & northwest)
      if (['0b1483324c68aff5014ae9b1', '3a37617ddc70d9b71bfa4e68'].includes(contextId) && fieldName === 'amountToPay') {
        rules = [
          {
            required: true,
            message: "Please input Payment Amount!",
          },
          {
            validator: (_, value) => {
              if (value && value <= 0 && value > 40000) {
                if (value > 40000) {
                  return Promise.reject(new Error("Maximum payment amount allowed for invoicing is $40,000"));
                } else {
                  return Promise.reject(new Error("Payment amount is invalid"));
                }
              }
              return Promise.resolve();
            },
          }
        ]
      } else {
        rules = allRules[fieldName];
      }
    } else {
      rules = [
        {
          required: isRequired,
          message: `Please input a ${formLabel}`,
        },
      ];
    }

    return (
      <>
        <Row>
          {requiredStar}
          {txtLabel}
        </Row>
        <Form.Item
          initialValue={
            invoiceDetails[fieldName] ? invoiceDetails[fieldName] : null
          }
          className={styles.formItems}
          name={fieldName}
          rules={rules}
        >
          {
            Array.isArray(selectorOptions) && selectorOptions.length > 0 ?
              <Select
                style={{
                  width: "100%",
                }}
                value={invoiceDetails[fieldName]}
                onChange={(e) => handleLinkedChange({ inputVal: e, fieldName })}
                placeholder={isRequired ? null : "(Optional)"}
                options={selectorOptions.map(
                  (option) => ({
                    value: option,
                    label: fieldName === "amountToPay" ? `$${option}` : option,
                  })
                )}
              />
              :
              fieldName === 'amountToPay' ?
                <InputNumber
                  prefix='$'
                  value={invoiceDetails[fieldName]}
                  placeholder={isRequired ? null : "(Optional)"}
                  onChange={(e) => takeInput({ inputVal: e, fieldName })}
                  stringMode={true}
                  style={{ width: '100%' }}
                  step={0.01}
                  precision={2}
                />
                :
                <Input
                  value={invoiceDetails[fieldName]}
                  placeholder={isRequired ? null : "(Optional)"}
                  onChange={(e) => takeInput({ inputVal: e.target.value, fieldName })}
                />
          }
        </Form.Item>
      </>
    );
  };

  const takeInput = ({ inputVal, fieldName }) => {
    if (fieldName === "amountToPay") {
      inputVal = Number(inputVal);
      setOrderSubtotal(inputVal);
    }
    setInvoiceDetails((prevState) => ({ ...prevState, [fieldName]: inputVal }));
  };

  const formatServiceFee = (digitalFeeObj) => {
    const feePercentage = digitalFeeObj?.serviceFeeInfo?.feePercentage;
    const feeCents = digitalFeeObj?.serviceFeeInfo?.feeCents;

    const percentageString = feePercentage ? `${feePercentage}%` : "";
    const feeString = feeCents ? `$${(feeCents / 100).toFixed(2)}` : "";

    if (percentageString && feeString) {
      return `${percentageString} + ${feeString}`;
    } else {
      return percentageString || feeString;
    }
  };

  let imgWidth = 180;

  if (
    stadiumData?._id === "vWbhEfxtsfbK82CKv" ||
    stadiumData?._id === "CakDLtXvDiB7xZRCR"
  ) {
    imgWidth = 280;
  }

  if (!stadiumData) {
    return null;
  }

  const formFields = [];
  for (const [key, value] of Object.entries(stadiumData?.invoiceTemplate)) {
    const newObj = { ...value, key };
    formFields.push(newObj);
  }

  // Sort the formFields array based on the renderPosition property
  formFields.sort((a, b) => (a.renderPosition || 0) - (b.renderPosition || 0));

  //For confirm service fee show different description text for The Mental Health Gala Payments
  const tooltipText = "Your transaction is processed by a secure third-party provider. Your personal and financial information is secure and PCI DSS compliant when processed through our payment portal. A transaction receipt will be sent to the contact information in the payment form."

  const { _id: contextId } = brandContextData;

  // Show image and description
  const ticketingHackIds = ['Lck4NNrxTBKHNTxBc', 'J6FycXWYqurpzn78J', 'yafeTmAJreutihkww'];

  let titleImage = '';
  let titleText = '';

  if (stadiumId === 'Lck4NNrxTBKHNTxBc') {
    titleImage = 'https://quetzalpay.s3.ca-central-1.amazonaws.com/stadium/fdi-fdi-unnamed.jpg';
    titleText = `Join us this fall for The Clinician Reset—a transformative retreat designed specifically for health and mental health professionals. Whether you choose to attend one day or both, this flexible experience offers the space, support, and tools you need to reset, recharge, and elevate your practice.

      This retreat offers the space to reconnect with your practice, refresh your approach to client care, enhance your business strategies, and walk away with practical tools that will empower you to thrive both personally and professionally.
      Event Highlights 

      Day One: Horseshoe Valley - Reconnect with Purpose & Practice

      Includes: Lunch, snacks, refreshments, and space to reflect and reset.
      Spend the day exploring your professional identity—what fuels your passion, where your strengths lie, and what may be holding you back. Through guided reflection, real-world business insights, and connection with fellow practitioners, you'll gain the clarity and confidence to take your practice to the next level.

      Day One & Two: Restore Your System @ Vettä Nordic Spa

      Includes: Day One + Join Day Two for nourishing breakfast, guided meditation,and full access to the tranquil Vettä Nordic Spa.
      Give yourself the care you so often give to others. Start your day with grounding meditation and a healthy breakfast, followed by deep relaxation and restoration in a peaceful Nordic spa setting.

      Note: Accommodations are not included in the ticket price. However, we've secured preferred rates for retreat attendees! Booking details will be provided in your registration confirmation email.

      Special Pricing for Students & New Graduates

      We're excited to offer reduced rates for students and recent graduates (within the past 18 months)! 
      Student or graduate status will be confirmed upon registration. If eligibility cannot be verified, the full retreat rate will apply.

      Payments can be made via e-transfer to: admin@thenestcollective.ca
      Please include your full name and which ticket package you're selecting in the transfer notes.

      Ready to Reset & Recharge?

      This is more than a retreat—it's an investment in your future. Space is limited, so don't wait!
      Register today and join us this fall to reset, refresh, and reignite your passion for the work you do.

      Questions? Contact us at hello@gibsoncounselling.ca

      Please note: Registrations received after October 7th may be subject to limitations, including the inability to accommodate dietary restrictions and restricted access to Vetta Nordic Spa, dependent on availability. Additional information will be provided via email following registration
    `
  } else if (stadiumId === 'J6FycXWYqurpzn78J') {
    titleImage = 'https://quetzalpay.s3.ca-central-1.amazonaws.com/stadiumFeaturedImages/stadiumFeaturedImage-unnamed.jpg'
    titleText = `Remembering Ourselves Home
      A 5-Day IFS-Informed Retreat for Women
      📅 May 11-16, 2026
      📍 Elmhurst Inn, Ingersoll, ON
      What are you doing next May?
      This is your invitation to reconnect, rediscover, and rise.
      Join a powerful, soul-nourishing retreat led by senior IFS trainers Kathy Cox and Kay Gardner—designed for IFS-informed women ready to explore their relationship to their vision, soul calling, and life purpose.
      Remembering Ourselves Home is a deeply experiential retreat for women-identifying IFS students and professionals (Level 1 or equivalent), offering space to reflect, reset, and reclaim your personal power.
      Through movement, art, writing, time in nature, and heartfelt connection, you'll experience a mix of:
      Teachings rooted in Internal Family Systems
      Intimate sharing and group process
      Movement and somatic practices
      Nature-based connection and reflection
      Creative expression and personal ritual
      Whether you're navigating a life transition or feeling the call to reconnect with your deeper self, this retreat invites you to come home—home to your truth, your power, and your purpose.
      All women are welcome—no matter your age or phase of life.
      ---
      Cost: $4,495 CAD Deposit: $500 CAD (non-refundable, non-transferable)

      This includes:
      ✔️ Tuition and facilitation by Kay and Kathy
      ✔️ 5 nights of accommodations at the beautiful Elmhurst Inn
      ✔️ All meals and snacks
      ✔️ Access to retreat materials and guided practices
      ✔️ A supportive, nurturing environment rooted in creativity and connection
      To secure your spot, your deposit can be paid in one of two ways:
      E-transfer to Michelle@thenestcollective.ca
      Credit card 
      If you have selected double occupancy with another participant, please reach out to us with the other participant CC'd so we can offer you the updated pricing information and ensure we pair you together.
      If a payment plan would make this experience more accessible, please reach out to hello@gibsoncounselling.ca. We're happy to explore options with you.
      ---
      Otherwise, the Standard Payment Schedule is as follows: 
      Please note if any payment is not received within two weeks of the posted deadline—unless prior arrangements have been made—your spot may be offered to someone on the waitlist and your deposit will be forfeited.

      Non-Refundable Deposit
      A non-refundable, non-transferable deposit of $500 CAD per person is due once your registration has been accepted. This deposit secures your room and your place at the retreat.

      Second Payment
      Due: October 15, 2025
      Amount: $1,200 CAD per person
      This instalment is refundable until December 15th, 2025, should you choose to cancel.
      Note: If payment is not received within two weeks of this deadline—unless prior arrangements have been made—your spot may be offered to someone on the waitlist and your deposit will be forfeited.

      Third Payment
      Due: January 15th, 2025
      Amount: $1,200 CAD per person
      This payment is refundable only until February 15th, 2025.
      Note: If payment is not received within two weeks, and no prior arrangements have been made, your space will be forfeited and your deposit will not be refunded.

      Final Payment
      Due: March 15th, 2026
      Amount: Remaining balance (based on room type). For single occupancy, this is $1,595 CAD.
      There are no refunds for any reason as of the payment date.
      Note: If the final balance is not paid within two weeks of this deadline, without prior arrangements, your space will be offered to someone on the waitlist and all fees paid to date will be forfeited.
    `
  } else if (stadiumId === 'yafeTmAJreutihkww') {
    titleImage = 'https://quetzalpay.s3.ca-central-1.amazonaws.com/stadiumFeaturedImages/stadiumFeaturedImage-20240927_083836.jpg.webp'
    titleText = `IFS Workshop Series: An Introduction to Internal Family Systems for Clinicians

      Join us for a six-part experiential workshop designed to deepen your understanding and practice of Internal Family Systems (IFS) therapy. This immersive series blends theory, demonstration, and guided practice to support clinicians and helping professionals in integrating IFS principles into their clinical and personal work.

      Across three lecture days and three experiential practice sessions, participants will explore the foundational concepts of the IFS model, learn how to identify and work with different types of parts, and cultivate a deeper connection to Self-energy as the foundation for healing and therapeutic presence.

      Workshop Schedule

      June 1: Introduction to the Model  (Lecture day)
      June 2: Parts Mapping & Practice  (Experiental day)
      June 8: Working with Protectors (Lecture day)
      June 9: Interviewing Protectors (Experiental day)
      June 15: Vulnerable Parts & Therapist Parts (Lecture day)
      June 16: Who Shows Up in Your Work and Closing (Experiential day)

      Format:
      Lecture Days: 9:00 AM - 4:00 PM
      Experiential Days: 12:00 PM - 2:00 PM

      Who It's For:
      Clinicians, counsellors, and helping professionals who are new to IFS or looking to strengthen their foundational skills in the model through experiential learning and guided practice.

      Early Bird / Student Rate
      For registrations before March 30 or recent grads (must show proof of graduation within one year of program date)
      $795

      Standard Rate
      For general participants
      $950

      Group/Organizational Rate
      For three or more people joining from the same organization after March 30th
      $850/person

      Payment Options
      Participants may pay in full at the time of registration or select a two-payment plan, with 50% due at registration and the remaining 50% charged on March 30th.

      Refund Policy
      Refunds are available until March 30th, minus a $75 non-refundable administrative fee. After March 30th, all payments are non-refundable and non-transferable.
    `
  }


  return (
    <div>
      {!loadingBrandContextData && brandContextData && stadiumData ? (
        <Col
          xs={{ span: 24 }}
          sm={{ span: 20, offset: 2 }}
          md={{ span: 20, offset: 2 }}
          lg={{ span: 18, offset: 3 }}
          xl={{ span: 16, offset: 4 }}
          className={styles.container}
        >
          {stadiumData?.imageUrl ? (
            <Row justify={"center"} className={styles.logoContainer}>
              <Image
                width={mobile ? "50%" : imgWidth}
                preview={false}
                src={stadiumData.imageUrl}
                style={{
                  maxHeight: 180,
                  minHeight: 75,
                  objectFit: "contain",
                }}
                onClick={() => navigate("/")}
              />
            </Row>
          ) : null}
          <Col className={styles.content}>
            {stadiumData?.name ? (
              <Row style={{ marginBottom: 20, justifyContent: "center" }}>
                <Col
                  span={24}
                  style={{ justifyContent: "center", display: "flex" }}
                >
                  <Text style={{ fontWeight: "bold" }}>{ticketingHackIds.includes(stadiumId) ? '' : 'PAYEE'}</Text>
                </Col>
                <Col
                  span={24}
                  style={{ justifyContent: "center", display: "flex" }}
                >
                  <Text className={styles.grayText} style={{ fontSize: 20 }}>
                    {stadiumData.name}
                  </Text>
                </Col>
              </Row>
            ) : null}

            {
              ticketingHackIds.includes(stadiumId) ?
                <Row justify={"center"} className={styles.logoContainer}>
                  <Col
                    span={24}
                    style={{ justifyContent: "center", display: "flex" }}
                  >
                    <Image
                      width={mobile ? "50%" : imgWidth}
                      preview={false}
                      src={titleImage}
                      style={{
                        maxHeight: 180,
                        minHeight: 75,
                        objectFit: "contain",
                      }}
                    />
                  </Col>
                  <Col
                    span={24}
                    style={{ justifyContent: "center", display: "flex" }}
                  >
                    <Text className={styles.grayText} style={{ fontSize: 14, marginTop: 20, whiteSpace: 'pre-line' }}>
                      {titleText}
                    </Text>
                  </Col>
                </Row>
                : null
            }
            <Form
              form={form}
              layout="vertical"
              size="large"
              name="basic"
              wrapperCol={{
                span: 24,
              }}
              initialValues={{
                remember: true,
              }}
              autoComplete="off"
              onFieldsChange={handleFormChange}
            >
              {formFields.map((field) => (
                <React.Fragment key={field.key}>
                  {_renderFormField(field, field.key, formFields)}
                </React.Fragment>
              ))}

              {showSaveCheckbox ? (
                <Checkbox
                  onChange={(e) => setSaveInfoForNextTime(e.target.checked)}
                  style={{
                    marginBottom: 30,
                    "--background-color": "black",
                    "--border-color": "black",
                    color: "gray",
                  }}
                  checked={saveInfoForNextTime}
                >
                  Save info for next time
                </Checkbox>
              ) : null}

              {(['0b1483324c68aff5014ae9b1', '3a37617ddc70d9b71bfa4e68'].includes(contextId)) ?
                <Row
                  style={{ marginBottom: 30 }}
                >
                  <AiFillStar
                    fontSize={5}
                    color="red"
                    style={{ marginTop: 2, marginRight: 3 }}
                  />
                  <Text className={styles.disclaimerText}>{`Maximum payment amount allowed for invoicing is $40,000`}</Text>
                </Row>
                :
                null}

              {digitalFeeObj?.applyServiceFee && (digitalFeeObj?.serviceFeeInfo && digitalFeeObj?.serviceFeeInfo?.feeAcknowledgement) ? (
                <Col
                  xs={{ span: 22, offset: 1 }}
                  sm={{ span: 22, offset: 1 }}
                  md={{ span: 22, offset: 1 }}
                  lg={{ span: 18, offset: 3 }}
                  xl={{ span: 12, offset: 6 }}
                  style={{ marginBottom: 30 }}
                >
                  <Form.Item
                    name="agreement"
                    valuePropName="checked"
                    rules={
                      []
                      // [
                      //   {
                      //     validator: (_, value) => {
                      //       console.log("This is the value");
                      //       console.log(_, value);
                      //       if (value) {
                      //         Promise.resolve();
                      //       } else {
                      //         Promise.reject(
                      //           new Error(
                      //             `Please acknowledge the ${
                      //               digitalFeeObj?.serviceFeeInfo?.feeLabel
                      //                 ? digitalFeeObj.serviceFeeInfo.feeLabel
                      //                 : "Digital Service Fee"
                      //             }`
                      //           )
                      //         );
                      //       }
                      //     },
                      //   },
                      // ]
                    }
                  >
                    <Checkbox
                      onChange={(e) => setServiceFeeAgreement(e.target.checked)}
                      style={{
                        "--background-color": token.colorSecondary,
                        "--border-color": token.colorSecondary,
                        color: "gray",
                      }}
                      checked={serviceFeeAgreement}
                    >
                      I acknowledge the{" "}
                      {digitalFeeObj?.serviceFeeInfo?.feeLabel
                        ? digitalFeeObj.serviceFeeInfo.feeLabel
                        : "Digital Service Fee"}
                      <span style={{ color: "red" }}>*</span>
                    </Checkbox>
                    <Tooltip title={tooltipText}>
                      <FaInfoCircle
                        name="info"
                        style={{ fontSize: 15, color: "gray" }}
                      />
                    </Tooltip>
                  </Form.Item>
                </Col>
              ) : null}
              <Col
                xs={{ span: 22, offset: 1 }}
                sm={{ span: 22, offset: 1 }}
                md={{ span: 22, offset: 1 }}
                lg={{ span: 18, offset: 3 }}
                xl={{ span: 12, offset: 6 }}
                style={{ marginBottom: 40 }}
              >
                <Row justify={"space-between"}>
                  <Text className={styles.totalText}>Subtotal</Text>
                  <Text
                    className={
                      orderSubtotal ? styles.totalText : styles.redText
                    }
                  >
                    {orderSubtotal
                      ? `$${formatNumberWithCommas(orderSubtotal)}`
                      : "Please input payment amount"}
                  </Text>
                </Row>
                {digitalFeeObj?.applyServiceFee ? (
                  <Row justify={"space-between"}>
                    <Text className={styles.totalText}>
                      {digitalFeeObj?.serviceFeeInfo?.feeLabel
                        ? `${digitalFeeObj.serviceFeeInfo.feeLabel
                        } (${formatServiceFee(digitalFeeObj)})`
                        : `Digital Service Fee (${formatServiceFee(
                          digitalFeeObj
                        )})`}
                    </Text>
                    <Text className={styles.totalText}>
                      {orderSubtotal
                        ? `$${Number(serviceFeeToShow).toFixed(2)}`
                        : "..."}
                    </Text>
                  </Row>
                ) : null}
                <Divider className={styles.totalDivider} />
                <Row justify={"space-between"}>
                  <Text className={styles.totalText}>Total</Text>
                  <Text className={styles.totalText}>
                    {orderSubtotal
                      ? `$${formatNumberWithCommas(orderTotalWithEverything)}`
                      : "..."}
                  </Text>
                </Row>
                <Button
                  disabled={!submittable}
                  htmlType="submit"
                  className={styles.submitButton}
                  onClick={createTheOrder}
                >
                  {submitButtonTitle}
                </Button>
              </Col>
            </Form>
          </Col>
        </Col>
      ) : null}
    </div>
  );
};

export default InvoicePaymentForm;
